// Онбординг «Быстрая настройка» (HQ-1092, история HQ-1056; Figma 772:22005): когда показывать, порядок шагов, отметка
// «пройден / пропущен» и события воронки. Чистая логика без React; вид — OnboardingScreen и его шаги.
// Порядок: старт → шаги выбора сервера (screens/onboarding/, как у PickTeams HQ-726) → уведомления → пейвол (только
// при offer ≠ null и неактивной подписке) → главная. Шага входа нет: человек известен по Telegram (initData).
import { track } from '../../analytics/track';
import type { PickStep } from '../../api/onboarding';
import { favoritesCount, type FavoritesSet } from '../../session/favorites';

/** Маршрут онбординга: имя экрана в аналитике — onboarding_setup (шаблон ^onboarding_ словаря screen_name). */
export const ONBOARDING_ROUTE = '/onboarding/setup';
export const MARK_KEY = 'cricket.onboarding';
/** done — пройден до конца, skipped — «Skip» на старте, returning — избранное уже было (вернувшийся человек). */
export type OnboardingMark = 'done' | 'skipped' | 'returning';
const MARKS: readonly string[] = ['done', 'skipped', 'returning'];
/** flow_source событий: онбординг первого запуска (у мягкой карточки HQ-726 — home_card). */
export const FLOW_SOURCE = 'first_launch';
export const PAYWALL_SOURCE = 'onboarding';

export function onboardingMark(): OnboardingMark | null {
  try {
    const raw = localStorage.getItem(MARK_KEY);
    return raw && MARKS.includes(raw) ? (raw as OnboardingMark) : null;
  } catch { return null; }
}

let launchMark: OnboardingMark | null | undefined;
/** Отметка онбординга с прошлых запусков: читается один раз — при старте приложения (App), до записи этого запуска.
 *  По ней тур по приложению (HQ-2338) отличает нового человека от того, кто уже пользуется. */
export function priorOnboardingMark(): OnboardingMark | null {
  if (launchMark === undefined) launchMark = onboardingMark();
  return launchMark;
}

export function setOnboardingMark(mark: OnboardingMark): void {
  try { localStorage.setItem(MARK_KEY, mark); } catch { /* приватный режим — до перезапуска покажем ещё раз */ }
}

/** Запуск, который онбординг не перебивает: глубокая ссылка (startapp / dev ?route=) или вход сразу в мир игры. */
export const launchAllowsOnboarding = (p: { startParam: string | null | undefined; toWorld: boolean }): boolean =>
  !p.startParam && !p.toWorld;

/** offline — сессия есть, а набор на паузе без сети: «Нет сети» с «Retry» вместо вечного скелетона (HQ-1797). */
export type GateDecision = 'show' | 'skip' | 'wait' | 'offline';

/** Показывать ли онбординг на этом запуске: нет отметки И избранное пустое, запуск обычный. Сессии нет — аккаунта ещё
 *  нет и избранного тоже; сессия есть — ждём набор сервера (не пришёл совсем — не держим человека, пропускаем; запрос
 *  на паузе без сети — offline, сеть вернётся — решение по тем же правилам). */
export function gateDecision(p: {
  launch: boolean; mark: OnboardingMark | null; session: boolean; loaded: boolean; failed: boolean; paused?: boolean;
  favorites: FavoritesSet;
}): GateDecision {
  if (!p.launch || p.mark) return 'skip';
  if (!p.session) return 'show';
  if (p.failed) return 'skip';
  if (!p.loaded) return p.paused ? 'offline' : 'wait';
  return favoritesCount(p.favorites) > 0 ? 'skip' : 'show';
}

export type Stage =
  | { kind: 'start'; key: 'start' }
  | { kind: 'pick'; key: string; step: PickStep }
  /** шаги сервера ещё грузятся или не пришли — на их месте скелетон / ошибка */
  | { kind: 'steps'; key: 'steps' }
  | { kind: 'notifications'; key: 'notifications' }
  | { kind: 'paywall'; key: 'paywall' };

/** Шаги по порядку. steps: undefined — ответа ещё нет (место под скелетон), [] — шагов нет. paywall: показать ли пейвол
 *  (offer ≠ null и подписка не активна; пока ответа нет — место держим, иначе прогресс прыгнет назад). */
export function buildStages(p: { steps: PickStep[] | undefined; paywall: boolean }): Stage[] {
  const picks: Stage[] = p.steps ? p.steps.map((step) => ({ kind: 'pick', key: step.key, step })) : [{ kind: 'steps', key: 'steps' }];
  return [
    { kind: 'start', key: 'start' }, ...picks, { kind: 'notifications', key: 'notifications' },
    ...(p.paywall ? [{ kind: 'paywall', key: 'paywall' } as Stage] : []),
  ];
}

/** Заливка индикатора по макету, % (HQ-1973, как Android OnboardingModel.kt progressPercent): «Игроки» — Progress
 *  I772:22062;14835:86065 (76 из 100), «Лиги» — I772:22096;14835:86109 (92), «Уведомления» — 772:22127 (100). */
export const PROGRESS_FIGMA = { player: 76, league: 92, notifications: 100 } as const;

const figmaShare = (s: Stage): number | undefined =>
  (s.kind === 'notifications' ? PROGRESS_FIGMA.notifications
    : s.kind === 'pick' && (s.step.entity_type === 'player' || s.step.entity_type === 'league') ? PROGRESS_FIGMA[s.step.entity_type]
      : undefined);

/** Прогресс в нижней панели, % заливки (HQ-1939, HQ-1973). Считаем только шаги с панелью (выбор и уведомления; старт
 *  и пейвол без панели). Шаг вида из макета — его число; иной шаг (новый entity_type сервера, повтор вида, скелетон) —
 *  доля по числу шагов, если она между предыдущим шагом и следующим числом макета, иначе середина между ними. Так
 *  заливка растёт от шага к шагу, «Уведомления» — 100, всё до них меньше 100. */
export function progressOf(stages: Stage[], index: number): number {
  const panel = stages.map((s, i) => [s, i] as const).filter(([s]) => s.kind !== 'start' && s.kind !== 'paywall');
  let prev = 0;
  for (let n = 0; n < panel.length; n++) {
    const [stage, at] = panel[n]!;
    let share = figmaShare(stage);
    if (share === undefined || share <= prev) {
      const next = panel.slice(n + 1).map(([s]) => figmaShare(s)).find((v) => v !== undefined && v > prev)
        ?? PROGRESS_FIGMA.notifications;
      const byCount = ((n + 1) / panel.length) * 100;
      share = byCount > prev && byCount < next ? byCount : (prev + next) / 2;
    }
    if (at >= index) return at === index ? share : prev;
    prev = share;
  }
  return prev;
}

/** Подпись главной кнопки шага: «Next (2)»; ничего не выбрано — «Next». */
export const countLabel = (label: string, count: number, format: (label: string, n: number) => string): string =>
  (count > 0 ? format(label, count) : label);

/** secondary шага сервера: action back — кнопка «Назад», иное — пропуск шага (подпись сервера). */
export const stepSkipLabel = (step: PickStep): string | null =>
  (step.secondary && step.secondary.action !== 'back' ? step.secondary.label : null);

/** Поиск в шаге (772:22173–22177): фильтр по уже полученным items / groups, без учёта регистра, без новой ручки.
 *  Турниры: совпала страна — группа целиком, иначе — только совпавшие турниры. Пустой запрос — шаг как есть. */
export function filterStep(step: PickStep, query: string): PickStep {
  const q = query.trim().toLocaleLowerCase();
  if (!q) return step;
  const hit = (...texts: Array<string | null | undefined>) => texts.some((s) => (s ?? '').toLocaleLowerCase().includes(q));
  switch (step.entity_type) {
    case 'team': return { ...step, items: step.items.filter((i) => hit(i.name, i.code)) };
    case 'player': return { ...step, items: step.items.filter((i) => hit(i.name, i.team?.name)) };
    case 'league': return {
      ...step,
      groups: step.groups
        .map((g) => (hit(g.name) ? g : { ...g, leagues: g.leagues.filter((l) => hit(l.name, l.code)) }))
        .filter((g) => g.leagues.length > 0),
    };
  }
}

export const isEmptyStep = (step: PickStep): boolean =>
  (step.entity_type === 'league' ? step.groups.length === 0 : step.items.length === 0);

// ---- События воронки (словарь v1, группа onboarding): step_name — ключ шага (start, ключ шага сервера,
// notifications, paywall), step_index — номер шага с 0 (старт), result — next / skip.
type StepInfo = { key: string; index: number; variant: string };
const params = (s: StepInfo) => ({ step_name: s.key, step_index: s.index, variant: s.variant || undefined, flow_source: FLOW_SOURCE });

export const trackStepView = (s: StepInfo) => track('onboarding_step_view', params(s));
export function trackStepComplete(s: StepInfo, result: 'next' | 'skip', selection?: number): void {
  track('onboarding_step_complete', { ...params(s), result, selection_count: selection });
}
export const trackComplete = (variant: string) => track('onboarding_complete', { variant: variant || undefined, flow_source: FLOW_SOURCE });
